import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import type { CartItem, CartState, CartMoney } from "../model/types";
import { initialCartState } from "../model/types";
import { cartBarReducer, calculateSubtotal } from "../model/cartBarReducer";

type AddItemOptions = { mergeById?: boolean };

type CartMergePolicy = "replace" | "merge" | "prefer_client" | "prefer_server";

type CartBarContextValue = {
  state: CartState;
  addItem: (item: CartItem, options?: AddItemOptions) => void;
  removeItem: (id: string) => void;
  updateQty: (id: string, qty: number) => void;
  clear: () => void;
  moveToSaved: (id: string) => void;
  moveToCart: (id: string) => void;
  setNotes: (id: string, notes: string) => void;
  subtotalAmount: CartMoney;
  totalAmount: CartMoney;
  mergeCart: (serverCart: CartState, policy?: CartMergePolicy) => void;
};

const CartBarContext = createContext<CartBarContextValue | undefined>(undefined);

const STORAGE_KEY = "hagd_cartBar_v1";

function isValidState(data: unknown): data is CartState {
  if (!data || typeof data !== "object") return false;
  const s = data as CartState;
  return Array.isArray(s.items) && Array.isArray(s.savedForLater);
}

function useDebouncedEffect(
  effect: () => void,
  deps: React.DependencyList,
  delay: number
) {
  const timeoutRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = window.setTimeout(effect, delay);
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, deps);
}

export function CartBarProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(cartBarReducer, initialCartState);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (isValidState(parsed)) {
          dispatch({ type: "LOAD_STATE", payload: parsed });
        }
      }
    } catch (err) {
      // Invalid stored data, ignore
    }
  }, []);

  // Save to localStorage whenever state changes
  useDebouncedEffect(
    () => {
      try {
        const isEmpty =
          state.items.length === 0 && state.savedForLater.length === 0;
        if (isEmpty) {
          localStorage.removeItem(STORAGE_KEY);
        } else {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        }
      } catch (err) {
        // Storage quota exceeded or other error, ignore
      }
    },
    [state],
    500
  );

  const addItem = useCallback(
    (item: CartItem, options?: AddItemOptions) => {
      dispatch({
        type: "ADD_ITEM",
        payload: { item, mergeById: options?.mergeById ?? true },
      });
    },
    []
  );

  const removeItem = useCallback((id: string) => {
    dispatch({ type: "REMOVE_ITEM", payload: { id } });
  }, []);

  const updateQty = useCallback((id: string, qty: number) => {
    dispatch({ type: "UPDATE_QTY", payload: { id, qty } });
  }, []);

  const clear = useCallback(() => {
    dispatch({ type: "CLEAR_CART" });
  }, []);

  const moveToSaved = useCallback((id: string) => {
    dispatch({ type: "MOVE_TO_SAVED", payload: { id } });
  }, []);

  const moveToCart = useCallback((id: string) => {
    dispatch({ type: "MOVE_TO_CART", payload: { id } });
  }, []);

  const setNotes = useCallback((id: string, notes: string) => {
    dispatch({ type: "SET_NOTES", payload: { id, notes } });
  }, []);

  const mergeCart = useCallback(
    (serverCart: CartState, policy: CartMergePolicy = "merge") => {
      dispatch({ type: "MERGE_CART", payload: { serverCart, policy } });
    },
    []
  );

  const subtotalAmount = useMemo(() => {
    return calculateSubtotal(state.items);
  }, [state.items]);

  const totalAmount = useMemo(() => {
    return subtotalAmount;
  }, [subtotalAmount]);

  const value = useMemo(
    () => ({
      state,
      addItem,
      removeItem,
      updateQty,
      clear,
      moveToSaved,
      moveToCart,
      setNotes,
      subtotalAmount,
      totalAmount,
      mergeCart,
    }),
    [
      state,
      addItem,
      removeItem,
      updateQty,
      clear,
      moveToSaved,
      moveToCart,
      setNotes,
      subtotalAmount,
      totalAmount,
      mergeCart,
    ]
  );

  return (
    <CartBarContext.Provider value={value}>{children}</CartBarContext.Provider>
  );
}

export function useCartBar(): CartBarContextValue {
  const context = useContext(CartBarContext);
  if (context === undefined) {
    throw new Error("useCartBar must be used within a CartBarProvider");
  }
  return context;
}
